/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input, Modal, Select, SelectOption } from "rizzui";
import toast from "react-hot-toast";
import useApi from "@/hooks/useApi";
import AdCreditRepository from "@/utilities/repositories/AdCredit";
import { PiX } from "react-icons/pi";
import {
  EDURATION_TYPE,
  IUpdateAdCreditDTO,
  ECreditDeductionCategory,
  ECreditAddCategory,
} from "@/utilities/types/AdCredit";
import { DEFAULT_REASON_CREDIT } from "@/config/constants";

// Schema validation cho modal form
const updateUserCreditModalSchema = z
  .object({
    action: z.enum(["add", "deduct"], {
      required_error: "Please select an action",
    }),
    amount: z
      .string()
      .min(1, "Amount is required")
      .regex(/^\d+(\.\d{1,2})?$/, "Please enter a valid number"),
    validity: z.string().optional(),
    validityUnit: z.nativeEnum(EDURATION_TYPE).default(EDURATION_TYPE.DAY),
    reason: z.string().optional(),
    deductCategory: z.nativeEnum(ECreditDeductionCategory).optional(),
    addCategory: z.nativeEnum(ECreditAddCategory).optional(),
  })
  .refine(
    (data) => {
      if (data.action === "deduct") {
        return !!data.deductCategory;
      }
      return true;
    },
    {
      message: "Category is required for deduction",
      path: ["deductCategory"],
    },
  )
  .refine(
    (data) => {
      if (data.action === "add") {
        return !!data.addCategory;
      }
      return true;
    },
    {
      message: "Category is required for adding credits",
      path: ["addCategory"],
    },
  );

type UpdateUserCreditModalData = z.infer<typeof updateUserCreditModalSchema>;

const validityUnitOptions = [
  { label: "day", value: EDURATION_TYPE.DAY },
  { label: "month", value: EDURATION_TYPE.MONTH },
];

const deductCategoryOptions = [
  { label: "Google Ads", value: ECreditDeductionCategory.GOOGLE_ADS },
  { label: "Lead", value: ECreditDeductionCategory.LEAD },
  { label: "Facebook", value: ECreditDeductionCategory.FACEBOOK },
  { label: "Ancillary", value: ECreditDeductionCategory.MISCELLANEOUS },
];

const addCategoryOptions = [
  { label: "Top-Up", value: ECreditAddCategory.TOP_UP },
  { label: "Google Ads Refund ", value: ECreditAddCategory.REFUND },
  { label: "Bonus", value: ECreditAddCategory.BONUS },
];

interface UpdateUserCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  userId: string;
}

export default function UpdateUserCreditModal({
  isOpen,
  onClose,
  onSuccess,
  userId,
}: UpdateUserCreditModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    control,
  } = useForm<UpdateUserCreditModalData>({
    resolver: zodResolver(updateUserCreditModalSchema),
    defaultValues: {
      action: "add",
      validityUnit: EDURATION_TYPE.DAY,
      addCategory: ECreditAddCategory.TOP_UP,
      deductCategory: ECreditDeductionCategory.GOOGLE_ADS,
    },
  });

  const { request: updateUserCredit } = useApi({
    request: AdCreditRepository.addOrDeductUserCredit,
    enableToast: false,
  });

  const selectedAction = watch("action");

  const onSubmit = async (data: UpdateUserCreditModalData) => {
    if (!userId) {
      toast.error("User ID is required");
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading("Updating user credit...");

    try {
      // Set default reason if none provided
      let reason = data.reason;
      if (!reason || reason.trim() === "") {
        reason =
          data.action === "deduct" ? DEFAULT_REASON_CREDIT.DEDUCT : undefined;
      }

      const updateData: IUpdateAdCreditDTO = {
        userId,
        amount: parseFloat(data.amount),
        duration: data.validity ? parseInt(data.validity) : undefined,
        durationType: data.validity ? data.validityUnit : undefined,
        reason,
        category:
          data.action === "deduct" ? data.deductCategory : data.addCategory,
      };
      await updateUserCredit({
        action: data.action,
        updateData,
      });

      toast.success("User credit updated successfully", { id: toastId });
      reset();
      onClose();
      onSuccess?.();
    } catch (error: any) {
      toast.error(error?.message || "Failed to update user credit", {
        id: toastId,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Update User Credit
          </h2>
          <Button
            variant="text"
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-md"
          >
            <PiX className="h-5 w-5" />
          </Button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(onSubmit)();
          }}
          className="space-y-6"
        >
          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              onClick={() => {
                setValue("action", "add");
              }}
              className={`px-6 py-2 rounded-md font-medium ${
                selectedAction === "add"
                  ? "bg-black text-white"
                  : "bg-white text-black border border-gray-300 hover:bg-gray-50"
              }`}
            >
              Add
            </Button>
            <Button
              type="button"
              onClick={() => {
                setValue("action", "deduct");
              }}
              className={`px-6 py-2 rounded-md font-medium ${
                selectedAction === "deduct"
                  ? "bg-black text-white"
                  : "bg-white text-black border border-gray-300 hover:bg-gray-50"
              }`}
            >
              Deduct
            </Button>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Amount
            </label>
            <Input
              {...register("amount")}
              placeholder="Enter amount"
              className="w-full"
              error={errors.amount?.message}
              type="text"
            />
          </div>

          {/* Validity */}
          {watch("action") === "add" && (
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Validity
              </label>
              <div className="flex gap-2">
                <Input
                  {...register("validity")}
                  placeholder="Enter validity"
                  className="flex-1"
                  error={errors.validity?.message}
                  type="text"
                />
                <Controller
                  name="validityUnit"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={(option: SelectOption) =>
                        field.onChange(option.value)
                      }
                      options={validityUnitOptions}
                      displayValue={(selectedValue: string) => {
                        const option = validityUnitOptions.find(
                          (opt) => opt.value === selectedValue,
                        );
                        return <span>{option?.label || selectedValue}</span>;
                      }}
                      className="w-24"
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* Category - For deduct action */}
          {watch("action") === "deduct" && (
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Category
              </label>
              <Controller
                name="deductCategory"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(option: SelectOption) =>
                      field.onChange(option.value)
                    }
                    options={deductCategoryOptions}
                    displayValue={(selectedValue: ECreditDeductionCategory) => {
                      const option = deductCategoryOptions.find(
                        (opt) => opt.value === selectedValue,
                      );
                      return <span>{option?.label || "Select category"}</span>;
                    }}
                    placeholder="Select category"
                    className="w-full"
                    error={errors.deductCategory?.message}
                  />
                )}
              />
            </div>
          )}

          {/* Category - For add action */}
          {watch("action") === "add" && (
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Category
              </label>
              <Controller
                name="addCategory"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(option: SelectOption) =>
                      field.onChange(option.value)
                    }
                    options={addCategoryOptions}
                    displayValue={(selectedValue: ECreditAddCategory) => {
                      const option = addCategoryOptions.find(
                        (opt) => opt.value === selectedValue,
                      );
                      return <span>{option?.label || "Select category"}</span>;
                    }}
                    placeholder="Select category"
                    className="w-full"
                    error={errors.addCategory?.message}
                  />
                )}
              />
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Description
            </label>
            <Input
              {...register("reason")}
              placeholder="Enter description"
              className="w-full"
              error={errors.reason?.message}
              type="text"
            />
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <Button
              type="submit"
              className="bg-black text-white hover:bg-gray-800 px-8 py-2 rounded-md"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
